/**
 * Operations-dashboard toolbar card: the alarms/maintenance tab strip
 * (with badge counts), the quick-search box, the FilterBar quick + more
 * filters (the alarms tab's Status filter selects active vs. resolved; the
 * Alarms tree filter lives behind More filters), an optional maintenance-tab
 * control (the "Show all" time-window override, node — null outside that
 * tab), and the trailing grid-settings/export-CSV icon buttons. Purely
 * presentational — tab/search/filter state and the export handler live in
 * the page.
 *
 * Contains:
 * - OperationsToolbar — default export
 */

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import React from 'react';

import FilterBar from '@sophocles/components/FilterBar';
import DataGridToolbarShell from '@sophocles/data-grids/components/toolbar/DataGridToolbarShell';
import ToolbarIconButton from '@sophocles/data-grids/components/toolbar/ToolbarIconButton';

export default function OperationsToolbar({
  t,
  activeTab,
  onTabChange,
  alarmsBadgeCount,
  maintenanceBadgeCount,
  searchText,
  onSearchChange,
  quickFilters,
  moreFilters,
  onResetAll,
  maintenanceExtra,
  exporting,
  onExport,
  onShowColumnSettings,
}) {
  return (
    <DataGridToolbarShell variant="elevated" sx={{ mb: 0, flexShrink: 0 }}>
      <FilterBar
        tabs={
          <Tabs
            value={activeTab}
            onChange={onTabChange}
            sx={{
              minHeight: 32,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 32,
                py: 0,
                px: 1.5,
                borderRadius: 'var(--button-radius)',
                fontSize: '0.8rem',
                textTransform: 'none',
              },
              '& .MuiTab-root.Mui-selected': {
                backgroundColor: 'var(--md-surface-container-high)',
              },
            }}
          >
            <Tab
              value="alarms"
              label={
                <Badge
                  badgeContent={alarmsBadgeCount}
                  color="warning"
                  max={999}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15 } }}
                >
                  <Box sx={{ pr: 1.5 }}>{t('operationsDashboard.alarms', { defaultValue: 'Alarms' })}</Box>
                </Badge>
              }
            />
            <Tab
              value="maintenance"
              label={
                <Badge
                  badgeContent={maintenanceBadgeCount}
                  color="info"
                  max={999}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15 } }}
                >
                  <Box sx={{ pr: 1.5 }}>{t('operationsDashboard.maintenance', { defaultValue: 'Maintenance' })}</Box>
                </Badge>
              }
            />
          </Tabs>
        }
        dense
        search={
          <TextField
            size="small"
            variant="filled"
            hiddenLabel
            placeholder={t('operationsDashboard.search', { defaultValue: 'Search...' })}
            value={searchText}
            onChange={onSearchChange}
            slotProps={{
              input: {
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiFilledInput-root': {
                borderRadius: 'var(--button-radius)',
                backgroundColor: 'var(--color-gray-50)',
                border: '1px solid var(--button-outline-border)',
                height: 'var(--filter-chip-height)',
                '&:hover': { backgroundColor: 'var(--color-gray-50)' },
                '&.Mui-focused': {
                  backgroundColor: 'var(--color-white)',
                  borderColor: 'var(--button-outline-border-hover)',
                },
              },
              '& .MuiFilledInput-input': { py: 0 },
            }}
          />
        }
        quickFilters={quickFilters}
        moreFilters={moreFilters}
        onResetAll={onResetAll}
        trailing={
          <>
            {maintenanceExtra}
            <ToolbarIconButton tooltip={t('general.gridSettings', 'Grid settings')} onClick={onShowColumnSettings}>
              <TuneIcon sx={{ fontSize: 18 }} />
            </ToolbarIconButton>
            <ToolbarIconButton
              tooltip={exporting ? 'Exporting...' : 'Export CSV'}
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? <CircularProgress size={18} /> : <FileDownloadIcon sx={{ fontSize: 18 }} />}
            </ToolbarIconButton>
          </>
        }
      />
    </DataGridToolbarShell>
  );
}
